import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, MessageSquare, Phone, ShieldCheck, KeyRound, AlertTriangle } from "lucide-react";
import AuthCard from "@/components/jea/AuthCard";
import JeaButton from "@/components/jea/JeaButton";
import { useAuth } from "@/context/AuthContext";
import { extractApiErrorMeta, formatApiError } from "@/lib/apiClient";
import { cn } from "@/lib/utils";

const CHANNEL_META = {
  email:  { label: "Email",             icon: Mail,         caption: "Code sent to your inbox" },
  sms:    { label: "Text message (SMS)",icon: MessageSquare,caption: "Code sent to your phone" },
  voice:  { label: "Voice call",        icon: Phone,        caption: "We'll read the code aloud" },
  totp:   { label: "Authenticator app", icon: ShieldCheck,  caption: "Open your authenticator app" },
  backup: { label: "Backup code",       icon: KeyRound,     caption: "Use a single-use recovery code" },
};

const RESEND_COOLDOWN = 30;

const TwoFactorPage = () => {
  const location = useLocation();
  const nav = useNavigate();
  const { verify2FA, send2FACode, isAuthenticated, user } = useAuth();
  const pending_token  = location.state?.pending_token || "";
  const email          = location.state?.email || "";
  const availableChannels = location.state?.available_channels || ["email"];
  const primaryChannel = location.state?.primary_channel || availableChannels[0] || "email";
  const autoSent       = location.state?.auto_sent || null;

  const [channel, setChannel] = useState(primaryChannel);
  const [showPicker, setShowPicker] = useState(false);
  const [code, setCode] = useState("");
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [emailDegraded, setEmailDegraded] = useState(
    autoSent?.status === "quota_exhausted" || autoSent?.status === "degraded"
  );
  // Phase 10D — alternatives returned on a 503 from /2fa/send-code so we can
  // render clickable chips below the error. Falls back to the user's full
  // enrolled-channels list if the backend didn't send any (edge case).
  const [fallbackAlternatives, setFallbackAlternatives] = useState([]);
  const [cooldown, setCooldown] = useState(autoSent?.status === "sent" ? RESEND_COOLDOWN : 0);
  // Phase 10E — voice-fallback surfacing. The "I didn't get the text" button
  // becomes clickable 60s after any SMS send; clicking it reveals the "Call
  // me instead" chip which triggers send-code with channel="voice".
  const [smsVoiceOffered, setSmsVoiceOffered] = useState(false);
  const [smsDidNotArrive, setSmsDidNotArrive] = useState(false);
  const [smsWaitElapsed, setSmsWaitElapsed] = useState(0);
  const voiceEligible =
    availableChannels.includes("voice") ||
    (availableChannels.includes("sms") && user?.voice_fallback_enabled !== false);
  const inputs = useRef([]);

  const isCodeChannel = channel !== "backup";

  // Auto-open the picker when email delivery is degraded so the user sees
  // alternatives immediately. Only applies to users with >1 enrolled channel.
  useEffect(() => {
    if (emailDegraded && availableChannels.length > 1) {
      setShowPicker(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailDegraded]);

  useEffect(() => {
    if (isCodeChannel) inputs.current[0]?.focus();
  }, [channel]);

  // Resend cooldown ticker.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Phase 10E — SMS wait timer: enable "I didn't get the text" after 60s so
  // the user can request a voice-call fallback (if voice is eligible). Only
  // meaningful when the active channel is SMS.
  useEffect(() => {
    if (channel !== "sms") {
      setSmsWaitElapsed(0);
      setSmsDidNotArrive(false);
      return;
    }
    setSmsWaitElapsed(0);
    const t = setInterval(() => {
      setSmsWaitElapsed((s) => s + 1);
    }, 1000);
    return () => clearInterval(t);
  }, [channel]);

  if (isAuthenticated) return <Navigate to={user?.is_admin ? "/admin" : "/dashboard"} replace />;
  if (!pending_token) return <Navigate to="/login" replace />;

  const setDigit = (idx, val) => {
    const clean = (val || "").replace(/\D/g, "").slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[idx] = clean;
      return next;
    });
    if (clean && idx < 5) inputs.current[idx + 1]?.focus();
  };

  const onKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) inputs.current[idx - 1]?.focus();
    if (e.key === "ArrowLeft" && idx > 0) inputs.current[idx - 1]?.focus();
    if (e.key === "ArrowRight" && idx < 5) inputs.current[idx + 1]?.focus();
  };

  const onPaste = (e) => {
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!text) return;
    e.preventDefault();
    const arr = ["", "", "", "", "", ""];
    for (let i = 0; i < text.length; i++) arr[i] = text[i];
    setDigits(arr);
    inputs.current[Math.min(text.length, 5)]?.focus();
  };

  const resend = async () => {
    if (cooldown > 0 || sending) return;
    setSending(true);
    setError("");
    try {
      await send2FACode(pending_token, channel);
      toast.success(`New code sent via ${CHANNEL_META[channel]?.label || channel}.`);
      setCooldown(RESEND_COOLDOWN);
      setEmailDegraded(false);
      setFallbackAlternatives([]);
    } catch (err) {
      const meta = extractApiErrorMeta(err);
      const msg = meta.message || formatApiError(err);
      setError(msg);
      toast.error(msg);
      // Phase 10D — email quota exhausted or service degraded → surface
      // clickable fallback chips below the error.
      if (meta.code === "email_quota_exhausted" || meta.code === "email_service_degraded") {
        setEmailDegraded(true);
        const alts = Array.isArray(meta.alternatives) && meta.alternatives.length > 0
          ? meta.alternatives
          : availableChannels.filter((c) => c !== "email");
        setFallbackAlternatives(alts);
        if (alts.length > 0) setShowPicker(true);
      }
    } finally {
      setSending(false);
    }
  };

  const switchChannel = async (newChannel) => {
    // Normalize "backup_code" → "backup" so this helper is tolerant to either
    // naming (backend uses "backup", some older clients used "backup_code").
    const normalized = newChannel === "backup_code" ? "backup" : newChannel;
    setChannel(normalized);
    setShowPicker(false);
    setDigits(["", "", "", "", "", ""]);
    setCode("");
    setError("");
    setFallbackAlternatives([]);
    // For sms/voice/email, immediately send a fresh code on the new channel.
    if (normalized === "sms" || normalized === "voice" || normalized === "email") {
      try {
        setSending(true);
        await send2FACode(pending_token, normalized);
        toast.success(`Code sent via ${CHANNEL_META[normalized]?.label}.`);
        setCooldown(RESEND_COOLDOWN);
        if (normalized !== "email") setEmailDegraded(false);
      } catch (err) {
        const meta = extractApiErrorMeta(err);
        const msg = meta.message || formatApiError(err);
        setError(msg);
        toast.error(msg);
        if (meta.code === "email_quota_exhausted" || meta.code === "email_service_degraded") {
          setEmailDegraded(true);
          const alts = Array.isArray(meta.alternatives) && meta.alternatives.length > 0
            ? meta.alternatives
            : availableChannels.filter((c) => c !== normalized);
          setFallbackAlternatives(alts);
        }
        // Phase 10E — SMS send/verify failure → auto-surface voice fallback.
        if (normalized === "sms" && voiceEligible) {
          setSmsVoiceOffered(true);
        }
      } finally {
        setSending(false);
      }
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const finalCode = isCodeChannel ? digits.join("") : code.trim();
    if (isCodeChannel && finalCode.length !== 6) {
      setError("Enter all 6 digits.");
      return;
    }
    if (!isCodeChannel && finalCode.length < 10) {
      setError("Enter a backup code (format XXXXX-XXXXX).");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await verify2FA(pending_token, finalCode, channel);
      toast.success("Signed in — welcome back.");
      let dest = "/dashboard";
      try {
        const raw = window.localStorage.getItem("jea_auth");
        const parsed = raw ? JSON.parse(raw) : null;
        if (parsed?.user?.is_admin) dest = "/admin";
        else if (parsed?.user && parsed.user.onboarding_completed === false) dest = "/onboarding";
      } catch {}
      nav(dest, { replace: true });
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const ChannelIcon = CHANNEL_META[channel]?.icon || Mail;

  return (
    <AuthCard>
      <form onSubmit={submit} className="space-y-6" data-testid="twofactor-form">
        <div className="text-center space-y-2">
          <h2 className="text-[20px] font-bold text-white">Verify Your Identity</h2>
          <p className="text-[14px] text-text-secondary">
            <ChannelIcon className="inline w-4 h-4 -mt-0.5 mr-1.5 text-success" />
            {CHANNEL_META[channel]?.caption || "Enter your verification code"}
            {email && isCodeChannel && channel === "email" && (
              <span className="block text-text-muted text-[12px] mt-1">to {email}</span>
            )}
          </p>
        </div>

        {isCodeChannel ? (
          <div className="flex justify-between gap-2" onPaste={onPaste}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => (inputs.current[i] = el)}
                value={d}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => onKeyDown(i, e)}
                inputMode="numeric"
                maxLength={1}
                autoComplete="one-time-code"
                data-testid={`twofactor-digit-${i}`}
                className={cn(
                  "h-14 w-12 text-center text-[20px] font-bold text-white bg-bg-tertiary rounded-[8px] border transition-all duration-150",
                  d ? "border-success" : "border-border-default",
                  "focus:outline-none focus:border-success focus:shadow-[0_0_0_4px_rgba(46,204,113,0.30)]"
                )}
              />
            ))}
          </div>
        ) : (
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="XXXXX-XXXXX"
            autoComplete="off"
            maxLength={12}
            data-testid="twofactor-backup-input"
            className="w-full h-14 text-center text-[18px] font-mono tracking-widest text-white bg-bg-tertiary rounded-[8px] border border-border-default focus:outline-none focus:border-success focus:shadow-[0_0_0_4px_rgba(46,204,113,0.30)]"
          />
        )}

        {emailDegraded && channel === "email" && (
          <div
            className="flex items-start gap-2 text-[13px] text-warning bg-warning/10 border border-warning/30 rounded-md px-3 py-2.5"
            data-testid="twofactor-email-degraded-callout"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-warning">Email delivery is temporarily unavailable</div>
              <div className="text-[12px] text-text-secondary mt-0.5">
                {availableChannels.length > 1
                  ? "Switch to another method below."
                  : "Please try again in a few minutes."}
              </div>
            </div>
          </div>
        )}

        {error && (
          <div
            className="text-[13px] text-danger bg-danger/10 border border-danger/30 rounded-md px-3 py-2"
            data-testid="twofactor-error"
          >
            {error}
          </div>
        )}

        {/* Phase 10E — SMS → Voice fallback. Shows a prominent chip row when:
            • The current channel is SMS AND
            • Either the user has clicked "I didn't get the text", 60s have
              elapsed since the SMS was sent, OR an SMS send error surfaced.
            Voice is only offered when the user has enrolled voice OR has
            opted into voice fallback (default ON for SMS-enrolled users). */}
        {channel === "sms" && voiceEligible && (smsVoiceOffered || smsDidNotArrive || smsWaitElapsed >= 60) && (
          <div
            className="rounded-md border border-success/30 bg-success/5 px-3 py-3 space-y-2"
            data-testid="twofactor-voice-fallback"
          >
            <div className="text-[13px] text-white font-medium">
              Didn't get the text?
            </div>
            <div className="text-[12px] text-text-secondary">
              We can call you with the code instead.
            </div>
            <button
              type="button"
              onClick={() => switchChannel("voice")}
              disabled={sending}
              data-testid="twofactor-voice-fallback-chip"
              className={cn(
                "inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[13px] font-medium",
                "bg-success text-bg-primary border border-success",
                "hover:bg-success/90 hover:shadow-[0_0_0_4px_rgba(46,204,113,0.30)]",
                "transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed",
              )}
            >
              <Phone className="w-4 h-4" />
              <span>Call me instead</span>
            </button>
          </div>
        )}

        {/* Phase 10D — fallback-channel chips rendered after the error block
            when the backend returns 503 with a non-empty `alternatives` list.
            Priority: TOTP > SMS > Voice > Backup. First in list = green
            primary chip; the rest render as neutral chips below. */}
        {fallbackAlternatives.length > 0 && (() => {
          const PRIORITY = ["totp", "sms", "voice", "backup", "backup_code"];
          const normalized = fallbackAlternatives
            .map((c) => (c === "backup_code" ? "backup" : c))
            .filter((c) => CHANNEL_META[c]); // ignore unknown channels
          const unique = Array.from(new Set(normalized));
          const sorted = [...unique].sort(
            (a, b) => PRIORITY.indexOf(a) - PRIORITY.indexOf(b),
          );
          return (
            <div className="space-y-2" data-testid="twofactor-fallback-chips">
              <div className="text-[11px] font-medium uppercase tracking-wider text-text-muted">
                Try a different method
              </div>
              <div className="flex flex-wrap gap-2">
                {sorted.map((c, idx) => {
                  const Meta = CHANNEL_META[c]?.icon || Mail;
                  const label = CHANNEL_META[c]?.label || c;
                  const isPrimary = idx === 0;
                  const isBackup = c === "backup";
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => switchChannel(c)}
                      disabled={sending}
                      data-testid={`twofactor-fallback-chip-${c}`}
                      className={cn(
                        "inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-[13px] font-medium",
                        "border transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed",
                        isPrimary && !isBackup && [
                          "bg-success text-bg-primary border-success",
                          "hover:bg-success/90 hover:shadow-[0_0_0_4px_rgba(46,204,113,0.30)]",
                        ],
                        isPrimary && isBackup && [
                          "bg-warning/15 text-warning border-warning/50",
                          "hover:bg-warning/25 hover:border-warning",
                        ],
                        !isPrimary && [
                          "bg-bg-tertiary text-white border-border-default",
                          "hover:border-success hover:text-success",
                        ],
                      )}
                    >
                      <Meta className="w-4 h-4" />
                      <span>{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}

        <JeaButton type="submit" size="lg" fullWidth loading={loading} data-testid="twofactor-verify-button">
          Verify
        </JeaButton>

        <div className="text-center flex flex-col gap-2">
          {isCodeChannel && channel !== "totp" && (
            <button
              type="button"
              onClick={resend}
              disabled={cooldown > 0 || sending}
              className="text-[13px] text-text-secondary hover:text-success transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="twofactor-resend"
            >
              {sending ? "Sending…" : cooldown > 0 ? `Resend code (${cooldown}s)` : "Resend code"}
            </button>
          )}

          {/* Phase 10E — explicit "I didn't get the text" shortcut on SMS. */}
          {channel === "sms" && voiceEligible && !smsVoiceOffered && !smsDidNotArrive && (
            <button
              type="button"
              onClick={() => setSmsDidNotArrive(true)}
              className="text-[12px] text-text-muted hover:text-success transition-colors"
              data-testid="twofactor-sms-didnt-arrive"
            >
              I didn't get the text
            </button>
          )}

          {availableChannels.length > 1 || !availableChannels.includes("email") ? (
            <button
              type="button"
              onClick={() => setShowPicker((v) => !v)}
              className="text-[12px] text-text-muted hover:text-success transition-colors"
              data-testid="twofactor-switch-channel-toggle"
            >
              {showPicker ? "Hide options" : "Try a different method"}
            </button>
          ) : null}

          {showPicker && (
            <div className="mt-2 flex flex-col gap-1.5" data-testid="twofactor-channel-picker">
              {availableChannels.filter((c) => c !== channel).map((c) => {
                const Meta = CHANNEL_META[c]?.icon || Mail;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => switchChannel(c)}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-[8px] bg-bg-tertiary hover:bg-bg-secondary border border-border-default hover:border-success transition-all text-left"
                    data-testid={`twofactor-switch-to-${c}`}
                  >
                    <Meta className="w-4 h-4 text-success" />
                    <div className="flex-1">
                      <div className="text-[13px] font-medium text-white">{CHANNEL_META[c]?.label}</div>
                      <div className="text-[11px] text-text-muted">{CHANNEL_META[c]?.caption}</div>
                    </div>
                  </button>
                );
              })}
              {!availableChannels.includes("backup") && (
                <button
                  type="button"
                  onClick={() => switchChannel("backup")}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-[8px] bg-bg-tertiary hover:bg-bg-secondary border border-border-default hover:border-success transition-all text-left"
                  data-testid="twofactor-switch-to-backup"
                >
                  <KeyRound className="w-4 h-4 text-warning" />
                  <div className="flex-1">
                    <div className="text-[13px] font-medium text-white">Use a backup code</div>
                    <div className="text-[11px] text-text-muted">Single-use recovery code</div>
                  </div>
                </button>
              )}
            </div>
          )}

          <Link
            to="/login"
            className="text-[12px] text-text-muted hover:text-success transition-colors mt-2"
            data-testid="twofactor-back-to-login"
          >
            Back to login
          </Link>
        </div>
      </form>
    </AuthCard>
  );
};

export default TwoFactorPage;
